import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLocation, NavLink, useHistory } from 'react-router-dom';
import { classnames } from 'Utils/classnames';
import Icon from '../../../components/Icon';

const NavigationItem = ({ item, isHeader, isSubItem, isTeam, onToggleTeams, hasTeams, onTeamSelect, onClick }) => {
  const history = useHistory();
  const location = useLocation();
  const currentPath = location.pathname;
  const isHomeHeader = isHeader && item.name === 'Bilyoner Lig Merkezi';

  const buildTargetUrl = path => {
    const search = history?.location?.search || '';
    return search ? `${ path }${ search }` : path;
  };
  const isSelected = () => {
    if (isHomeHeader) {
      return currentPath === '/lig-merkezi';
    }
    
    if (isTeam && item.link) {
      const teamSlug = item.link.split('/').filter(Boolean).pop();
      const urlParts = currentPath.split('/').filter(Boolean);
      
      if (urlParts.length >= 2 && urlParts[1] === teamSlug) {
        return true;
      }
      return false;
    }
    
    if (isSubItem && item.link) {
      const leagueSlug = item.link.split('/').filter(Boolean).pop();
      const urlParts = currentPath.split('/').filter(Boolean);
      
      if (urlParts.length >= 2 && urlParts[1] === leagueSlug) {
        return true;
      }
      return false;
    }
    
    return currentPath === item.link;
  };

  const showLeagueIcon = isSubItem && item.icon;
  const showTeamIcon = isTeam && item.icon;
  const selected = isSelected();

  const itemClass = classnames({
    group__item: !isHeader && !isTeam && !isSubItem,
    group__header: isHeader,
    group__subitem: isSubItem,
    group__team: isTeam,
    'no-bullet': showLeagueIcon || showTeamIcon,
    'navigation-header': isHeader && !isHomeHeader,
    'navigation-home-header': isHomeHeader,
    'navigation-item-selected': selected && !isTeam,
    'navigation-team-selected': selected && isTeam,
    'navigation-item': !isHeader && !isTeam
  });
  

  const handleItemClick = useCallback(() => {
    if (isTeam && onTeamSelect && item.link) {
      onTeamSelect(item.leagueName);

      const pathParts = item.link.split('/').filter(Boolean);
      if (pathParts.length > 0) {
        const teamSlug = pathParts[pathParts.length - 1];
        history.push(`/lig-merkezi/${teamSlug}/puan-durumu`);
        
        if (onClick) onClick();
        return;
      }
    }
    
    if (onClick) onClick();
  }, [isTeam, onTeamSelect, item.leagueName, item.link, history, onClick]);

  const handleToggleTeams = useCallback(e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (onToggleTeams && typeof onToggleTeams === 'function') {
      onToggleTeams(item.name);
    }
  }, [item.name, onToggleTeams]);

  const selectedStyle = {
    fontWeight: selected ? '500' : '400',
    ...(item.color ? { '--bullet-color': item.color } : {})
  };

  const navLinkProps = {
    to: item.link || '#',
    title: item.name,
    className: itemClass,
    activeClassName: '',
    exact: true,
    onClick: handleItemClick,
    style: selectedStyle
  };

  if (item.name === 'Canlı Yayın Rehberi') {
    navLinkProps.to = buildTargetUrl(item.link);
  }

  const renderContent = () => {
    if (isHomeHeader) {
      return (
        <Icon iconName="svgBLogo" className="bilyoner-icon" />
      );
    }
    return (
      <>
        { (item.name === 'Canlı Yayın Rehberi') ? (
          <Icon iconName="svgGuideIcon" className="item-icon" />
        ) : showLeagueIcon && <img src={ item.icon } alt="" className="item-icon" /> }
        { showTeamIcon && <img src={ item.icon } alt="" className="team-icon" /> }

        <span>{ item.name } </span>
      </>
    );
  };

  if (isHomeHeader) {
    const headerTargetUrl = buildTargetUrl('/lig-merkezi');

    const handleHeaderClick = e => {
      e.preventDefault();

      history.push(headerTargetUrl);

      if (onClick) onClick();
    };

    return (
      <a
        href={ headerTargetUrl }
        className={ itemClass }
        onClick={ handleHeaderClick }
        style={ selectedStyle }
      >
        <Icon iconName="svgBLogo" className="bilyoner-icon" />
        <span>{ item.name }</span>
      </a>
    );
  }

  if (isTeam) {
    const teamSlug = item.slug.replace(/^\/lig-merkezi\//, '').split('/').pop();
    const teamTargetUrl = buildTargetUrl(`/lig-merkezi/${ teamSlug }/puan-durumu`);

    const handleTeamClick = e => {
      e.preventDefault();
      if (onTeamSelect) {
        onTeamSelect(item.leagueName);
      }

      history.push(teamTargetUrl);

      if (onClick) onClick();
    };

    return (
      <a
        href={ teamTargetUrl }
        className={ itemClass }
        onClick={ handleTeamClick }
        style={ selectedStyle }
      >
        { renderContent() }
      </a>
    );
  }

  if (isSubItem) {
    const leagueSlug = item.slug.replace(/^\/lig-merkezi\//, '').split('/').pop();
    const leagueTargetUrl = buildTargetUrl(`/lig-merkezi/${ leagueSlug }/puan-durumu`);

    const handleLeagueClick = e => {
      e.preventDefault();

      if (hasTeams) {
        onToggleTeams(item.name);
      }

      history.push(leagueTargetUrl);

      const { innerWidth } = window;
      const isMobile = innerWidth <= 640;
      if (isMobile && onClick) {
        onClick();
      }
    };

    return (
      <a
        href={ leagueTargetUrl }
        className={ itemClass }
        onClick={ handleLeagueClick }
        style={ selectedStyle }
      >
        { renderContent() }
        
        { hasTeams && (
          <span
            className="group__toggle-arrow"
            onClick={ e => {
              e.preventDefault();
              e.stopPropagation();
              onToggleTeams(item.name);
              return false;
            } }
          >
            <Icon iconName={ `svgArrowTo${item.isOpen ? 'Up' : 'Down'}` } />
          </span>
        ) }

      </a>
    );
  }

  return (
    <NavLink { ...navLinkProps }>
      { renderContent() }
      
      { hasTeams && (
        <span
          className="group__toggle-arrow"
          onClick={ e => {
            e.preventDefault();
            e.stopPropagation();
            handleToggleTeams(e);
            return false;
          } }
        >
          <Icon iconName={ `svgArrowTo${item.isOpen ? 'Up' : 'Down'}` } />
        </span>
      ) }

    </NavLink>
  );
};

NavigationItem.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string.isRequired,
    link: PropTypes.string,
    widgetsSlug: PropTypes.string,
    tag: PropTypes.string,
    color: PropTypes.string,
    icon: PropTypes.string,
    linkParams: PropTypes.object,
    isOpen: PropTypes.bool,
    leagueName: PropTypes.string,
    slug: PropTypes.string
  }).isRequired,
  isHeader: PropTypes.bool,
  isSubItem: PropTypes.bool,
  isTeam: PropTypes.bool,
  onToggleTeams: PropTypes.func,
  hasTeams: PropTypes.bool,
  onTeamSelect: PropTypes.func,
  onClick: PropTypes.func
};

NavigationItem.defaultProps = {
  isHeader: false,
  isSubItem: false,
  isTeam: false,
  onToggleTeams: () => {},
  hasTeams: false,
  onTeamSelect: undefined,
  onClick: () => {}
};

export default React.memo(NavigationItem);
